// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { RectilinearPolygon, Edge, Vector2D, Rectangle, BitBuffer, Line } from "./utils/index.js"

// @@imports-types
import { Config, EdgeType, EDGE_TYPES, MEMBRANE_TYPES, JOIN_TYPES, MembraneType } from "./types/index.js"

// @@body
class Deck extends RectilinearPolygon {
    features = {
        apexes: /** @type {Set.<Apex>} */ (new Set()),
        verticalDrains: [],
        horizontalDrains: [],
        protrusions: [],
        rooflights: [],
        parapets: [],
        boxGutters: []
    }
    details = {
        apexGradient: NaN,
        definedEdges: /** @type {Set.<Edge>} */ (new Set()),
        edgeTypes: /** @type {Map.<Edge, EdgeType>} */ (new Map())
    }

    apexes = /** @type {Set.<Apex>} */ (new Set())
    apexGradient = NaN
    #dimensionDefinedEdges = /** @type {Set.<Edge>} */ (new Set())
    #edgeTypes = /** @type {Map.<Edge, EdgeType>} */ (new Map())
    #parapets = /** @type {Map.<Edge, {width:number, height:number}>} */ (new Map()) // split into new type 
    rooflights = /** @type {Map.<*,*>} */ (new Map())

    /**
     * 
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D[]} [obj.vertices] Vertices for base shape of deck.
     * @param {boolean} [obj.isClosed] Is the base shape of the deck completed?
     */
    constructor({ vertices = [new Vector2D(0, 0)], isClosed = false } = {}) {
        super({ vertices, isClosed })
    }

    /**
     * 
     * @param {Vector2D} anchor 
     * @param {number} width 
     * @param {number} length 
     * @param {object} obj 
     * @param {number} [obj.rotation]
     * @param {number} [obj.upstandHeight]
     * @param {boolean} [obj.shouldRemoveMembrane]
     */
    addRooflight(anchor, width, length, {
        rotation = 0,
        upstandHeight,
        shouldRemoveMembrane = false
    } = {}) {
        const hole = new Rectangle(anchor, width, length)
        if (rotation !== 0) { hole.rotate(rotation) }

        this.addHole(hole)
        // this.rooflights.
    }

    // removeRooflight() {

    // }

    // transformRooflight(rectangle, {
    //     resizeWidth = rectangle.width,
    //     resizeHeight = rectangle.height,
    //     translation = new Vector2D(0, 0),
    //     rotation = 0
    // } = {}) {

    // }

    addDrain() {}

    addProtrusion() {}

    /**
     * 
     * @param {Apex} apex 
     */
    addApex(apex) {
        // 
        if (this.apexes.has(apex)) { throw new Error("Apex already exists") }
        if (isNaN(this.apexGradient)) { this.apexGradient = apex.gradient }
        else if (apex.gradient !== this.apexGradient) {
            throw new Error("Apex direction does not match existing apexes")
        }

        // 
        this.apexes.add(apex)
    }

    /**
     * 
     * @param {Apex} apex 
     */
    removeApex(apex) {
        this.apexes.delete(apex)
        if (this.apexes.size === 0) { this.apexGradient = NaN }
    }

    // Rewrite to return current deck instance flattened, and instead add deck
    // copy method which copies all features etc. 
    /**
     * 
     * @returns {RectilinearPolygon}
     */
    flatten() {
        // Copy existing deck instance and return rectilinear polygon result if
        // deck has no apex features.
        const flatDeck = this.copy()
        if (this.apexes.size === 0) { return flatDeck }

        // 
        const intercept = this.apexGradient === 0 ? "yIntercept" : "xIntercept"
        const apexes = [...this.apexes].sort((apexA, apexB) => {
            return apexA[intercept] - apexB[intercept]
        })

        // 
        const apexVertices = [new Vector2D(0, 0)]
        for (const apex of apexes) {
            apexVertices.push(new Vector2D(apex[intercept], apex.height))
        }
        apexVertices.push(new Vector2D(
            this.apexGradient === 0
                ? this.boundingBox.height
                : this.boundingBox.width,
            0
        ))

        // 
        const edgeFactors = /** @type {Map.<Edge, number>} */ (new Map())
        for (let i = 0; i < apexVertices.length - 1; i++) {
            const { x: currentX, y: currentY } = apexVertices[i]
            const { x: nextX, y: nextY } = apexVertices[i + 1]

            const start = this.apexGradient === 0
                ? new Vector2D(0, currentX) : new Vector2D(currentX, 0)
            const end = this.apexGradient === 0
                ? new Vector2D(0, nextX) : new Vector2D(nextX, 0)
            const eaveEdge = new Edge(start, end)

            const eaveFactor = Math
                .sqrt((nextX - currentX) ** 2 + (nextY - currentY) ** 2) /
                (nextX - currentX)

            for (const { value: edge } of flatDeck.edges) {
                const intersectionMagnitude = edge
                    .getParallelIntersectionMagnitude(eaveEdge, {
                        requireColinear: false
                    })
                const resizeFactor = (edge.magnitude + intersectionMagnitude *
                    (eaveFactor - 1)) / edge.magnitude

                const edgeFactor = edgeFactors.get(edge) || 1

                edgeFactors.set(edge, edgeFactor + resizeFactor - 1)
            }
        }

        const edgeMagnitudes = /** @type {Map.<Edge, number>} */ (new Map())
        for (const [edge, factor] of edgeFactors) {
            edgeMagnitudes.set(edge, edge.magnitude * factor)
        }
        for (const [edge, magnitude] of edgeMagnitudes) {
            flatDeck.resizeEdge(edge, Math.ceil(magnitude))
        }

        return flatDeck
    }

    /**
     * 
     * @param {Edge} edge 
     * @param {number} updatedMagnitude 
     */
    resizeEdge(edge, updatedMagnitude) {
        for (const apex of this.apexes) {
            const intersection = apex.getIntersection(Line.from(edge))
            if (intersection) {
                const intercept = apex.gradient === 0 ? "y" : "x"
                const factor = intersection[intercept] / edge.magnitude
                const deltaMagnitude = updatedMagnitude - edge.magnitude

                apex[`${intercept}Intercept`] += factor * deltaMagnitude
            }
        }
        super.resizeEdge(edge, updatedMagnitude)
        this.#dimensionDefinedEdges.add(edge)
    }

    /**
     * 
     * @param {Edge} edge 
     * @returns 
     */
    isSetEdgeLength(edge) {
        return this.#dimensionDefinedEdges.has(edge)
    }

    /**
     * 
     * @param {Edge} edge 
     * @param {object} obj
     * @param {MembraneType} [obj.membraneType]
     * @returns {EdgeType}
     */
    getEdgeType(edge, { membraneType } = {}) {
        // add error check if edge in polygon 
        const edgeType = this.#edgeTypes.get(edge) || EDGE_TYPES.none

        return membraneType === MEMBRANE_TYPES.fleeceBacked &&
            edgeType === EDGE_TYPES.seamJoin
                ? EDGE_TYPES.buttJoin
                : edgeType
    }

    /**
     * 
     * @param {Edge} edge 
     * @param {EdgeType} type 
     */
    setEdgeType(edge, type) {
        // add error check if edge in polygon 
        this.#edgeTypes.set(edge, type)
    }

    /**
     * 
     * @param {Edge} edge 
     * @returns {boolean}
     */
    isSetEdgeType(edge) {
        return !!this.#edgeTypes.get(edge)
    }

    /**
     * 
     * @param {Edge} edge 
     * @returns 
     */
    getParapet(edge) {
        return this.#parapets.get(edge)
    }

    /**
     * 
     * @param {Edge} edge 
     * @param {object} obj
     * @param {number} obj.width
     * @param {number} obj.height
     */
    setParapet(edge, { width, height }) {
        this.#parapets.set(edge, { width, height })
    }

    /**
     * 
     * @param {Edge} edge 
     * @param {Config} config
     * @returns {number}
     */
    getEdgeOverhang(edge, config) {
        const { membrane: { type: membraneType } } = config
        const rubberJoin = membraneType === MEMBRANE_TYPES.fleeceBacked
            ? JOIN_TYPES.butt
            : config.rubberJoin

        return this.edges.array.reduce((acc, cur) => {
            const parallelIntersectionFractions = edge
                .getParallelIntersectionFractions(cur)

            if (!parallelIntersectionFractions) { return acc }
            const { start, end } = parallelIntersectionFractions
            if (start >= 1 || end <= 0) { return acc }

            const edgeType = this.getEdgeType(cur, { membraneType })
            let overhang = config.overhangs[edgeType].client
            if (edgeType === EDGE_TYPES.parapetWall) {
                const { width, height } = {
                    ...config.parapet,
                    ...this.getParapet(edge)
                }
                overhang += width + height
            }
            acc = Math.max(overhang, acc)

            return acc
        }, config.overhangs[rubberJoin].client / 2)
    }

    /**
     * 
     * @param {Edge} edge 
     * @returns {number}
     */
    getEdgeNonIntersectingLength(edge) {
        return this.edges.array.reduce((acc, cur) => {
            return acc - edge.getParallelIntersectionMagnitude(cur)
        }, edge.magnitude)
    }

    /**
     * 
     * @returns {string} Serialized polygon.
     */
    serialize() {
        // Declare bit buffers for all features of the deck sized based on
        // assuming the maximum possible data required for each value:
        // - Vertices ~ 2 * 6 *bytes* (sign bit, 5 bit size declaration, 32 bit
        // integer). 2 coords per vertex for x and y.
        // - Edges ~ 5 *bits* per edge (only edge type required encoded as enum
        // with many "spare" values for future changes and additions).
        const vertexBuffer = new BitBuffer({ size: this.vertices.size * 12 })
        const edgeBuffer = new BitBuffer({
            size: Math.ceil(this.edges.size * 5 / 8)
        })

        // Write all vertices to required buffer.
        for (const { value: vertex } of this.vertices) {
            vertexBuffer.writeAbsolute(vertex.x, { signed: true })
            vertexBuffer.writeAbsolute(vertex.y, { signed: true })
        }

        // Write all edge types to required buffer.
        for (const { value: edge } of this.edges) {
            // Get edge type.
            const edgeType = this.#edgeTypes.get(edge)

            // Infer 5 bit enum value for given edge type. 

            // add 1 so that undefined/not found is 0 
            const edgeTypeEnum = Object
                .values(EDGE_TYPES)
                .indexOf(/** @type {any} */ (edgeType)) + 1

            edgeBuffer.write(edgeTypeEnum, { size: 5 })
        }

        return vertexBuffer.toString() + "+" +
            edgeBuffer.toString() + "+"
            // add details here (each detail should serialize itself and be 
            // separated by "!" char).
    }

    get isDefined() {
        let oddDefinedEdges = 0
        let evenDefinedEdges = 0
        for (const edge of this.#dimensionDefinedEdges) {
            const index = this.edges.indexOfValue(edge)
            if (index === - 1) { continue }
            index % 2 ? oddDefinedEdges++ : evenDefinedEdges++
        }
        const dimensions = oddDefinedEdges >= this.edges.size / 2 - 1 &&
            evenDefinedEdges >= this.edges.size / 2 - 1

        let types = true
        for (const { value: edge } of this.edges) {
            if (!this.#edgeTypes.has(edge)) {
                types = false
                break
            }
        }

        return { dimensions, types }
    }

    /**
     * 
     * @param {string} normalizedString 
     * @returns {Deck}
     */
    static fromNormalized(normalizedString) {
        // 
        let serializedString = ""
        const normalizedBuffer = BitBuffer.from(normalizedString)
        while (normalizedBuffer.writePointer < normalizedBuffer.bitLength) {
            serializedString += String.fromCharCode(normalizedBuffer.read(8))
        }

        // 
        const [,, serializedDeck] = serializedString.split(".")

        return Deck.fromSerialized(serializedDeck)
    }

    /**
     * 
     * @param {string} serializedString 
     * @returns {Deck}
     */
    static fromSerialized(serializedString) {
        const [
            serializedVertices,
            serializedEdges,
            serializedDetails
        ] = serializedString.split("+")

        const vertexBuffer = BitBuffer.from(serializedVertices)
        const edgeBuffer = BitBuffer.from(serializedEdges)

        const vertices = /** @type {Vector2D[]} */ ([])
        while (vertexBuffer.readPointer < vertexBuffer.bitLength) {
            const x = vertexBuffer.readAbsolute({ signed: true })
            const y = vertexBuffer.readAbsolute({ signed: true })
            if (isNaN(x) || isNaN(y)) { break }
            vertices.push(new Vector2D(x, y))
        }
        const deck = new Deck({ vertices, isClosed: true })

        for (const { value: edge } of deck.edges) {
            const edgeType = Object.values(EDGE_TYPES)[edgeBuffer.read(5) - 1]
            if (!edgeType) { continue }
            deck.setEdgeType(edge, edgeType)
        }

        return deck
    }
}

class Apex extends Line {
    /**
     * 
     * @param {number} gradient 
     * @param {number} intercept 
     * @param {number} height 
     */
    constructor(gradient, intercept, height, { isDefined = true } = {}) {
        if (gradient !== 0 && gradient !== Infinity) {
            throw new Error("Apex is not perpendicular to grid")
        }

        super(gradient, {
            [gradient === 0 ? "yIntercept" : "xIntercept"]: intercept
        })

        this.height = height
        this.isDefined = isDefined
    }

    serialize() {

    }

    static fromSerialized() {

    }
}

// @@exports
export { Deck, Apex }
